/**
 * Invoice Templates Component
 * Manages invoice templates with proper API integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { enhancedInvoiceService } from '../../services/enhancedInvoiceService';
import ProfessionalLoader from "../common/ProfessionalLoader";
import './InvoiceTemplates.css';

const InvoiceTemplates = () => {
  const { showError } = useNotifications();
  
  // State management
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Filters
  const [filters, setFilters] = useState({
    invoice_type: '',
    is_active: '',
    search: ''
  });

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await enhancedInvoiceService.getInvoiceTemplates(filters);
      
      if (response.success) {
        setTemplates(response.data || []);
      } else {
        showError(response.message || 'Failed to load templates', 'Error');
      }
    } catch (error) {
      showError('Failed to load templates', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showError]);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Handle template selection
  const handleTemplateSelect = (templateId, isSelected) => {
    setSelectedTemplates(prev => 
      isSelected 
        ? [...prev, templateId]
        : prev.filter(id => id !== templateId)
    );
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedTemplates(templates.map(template => template.id));
    } else {
      setSelectedTemplates([]);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  if (isLoading) {
    return (
      <div className="invoice-templates">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-templates">
      {/* Header */}
      <div className="templates-header">
        <div className="header-content">
          <h2>📋 Invoice Templates</h2>
          <p>Manage invoice templates for different types of invoices</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-outline"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          >
            <span className="btn-icon">{viewMode === 'grid' ? '📋' : '🔲'}</span>
            {viewMode === 'grid' ? 'List View' : 'Grid View'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="templates-filters">
        <div className="filters-content">
          <div className="filter-group">
            <label className="filter-label">Invoice Type</label>
            <select
              className="filter-select"
              value={filters.invoice_type}
              onChange={(e) => handleFilterChange({ invoice_type: e.target.value })}
            >
              <option value="">All Types</option>
              <option value="new_member">New Member</option>
              <option value="renewal">Renewal</option>
              <option value="late_fee">Late Fee</option>
              <option value="special_event">Special Event</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="filter-select"
              value={filters.is_active}
              onChange={(e) => handleFilterChange({ is_active: e.target.value })}
            >
              <option value="">All Statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <input
              type="text"
              className="filter-input"
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              placeholder="Search templates..."
            />
          </div>
          
          <div className="filters-actions">
            <button
              className="btn btn-outline"
              onClick={() => handleFilterChange({
                invoice_type: '',
                is_active: '',
                search: ''
              })}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedTemplates.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-actions-content">
            <span className="selected-count">
              {selectedTemplates.length} template(s) selected
            </span>
            <div className="bulk-controls">
              <button
                className="btn btn-outline"
                onClick={() => setSelectedTemplates([])}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="templates-content">
        {viewMode === 'grid' ? (
          <div className="templates-grid">
            {/* Template cards removed - component deleted */}
            <div className="empty-state">
              <p>Template grid view not available</p>
            </div>
          </div>
        ) : (
          <div className="templates-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedTemplates.length === templates.length && templates.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>Template Name</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Default Amount</th>
                  <th>Account Code</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(template => (
                  <tr key={template.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedTemplates.includes(template.id)}
                        onChange={(e) => handleTemplateSelect(template.id, e.target.checked)}
                      />
                    </td>
                    <td>{template.template_name}</td>
                    <td>{template.invoice_type}</td>
                    <td>{template.description_template}</td>
                    <td>${template.default_amount}</td>
                    <td>{template.account_code}</td>
                    <td>
                      <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Empty State */}
      {templates.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Templates</h3>
          <p>No invoice templates found for the selected criteria.</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplates;
